import { lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { DrawerProvider } from './Drawer';
import { Layout } from './Layout';
import { PortalShell } from './PortalShell';
import { moduleDef, visibleModules } from './modules';
import { PAGES, SCHEDULER_BASE, canSee, homePathFor, type PageId } from './routes';
import { useIsPhone } from './useIsPhone';
import type { PortalModule } from '../data/database.types';
import { Spinner } from '../ui/components';
import { SetupPage } from '../pages/auth/SetupPage';
import { SignInPage } from '../pages/auth/SignInPage';
import { NoAccessPage } from '../pages/auth/NoAccessPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';
import { PortalHome } from '../pages/portal/PortalHome';

// Pages load on demand so the first paint ships only the shell.
const DocumentsPage = lazy(() => import('../pages/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const DocumentPage = lazy(() => import('../pages/documents/DocumentPage').then((m) => ({ default: m.DocumentPage })));
const AnalyticsLayout = lazy(() => import('../pages/analytics/AnalyticsLayout').then((m) => ({ default: m.AnalyticsLayout })));
const OverviewPage = lazy(() => import('../pages/analytics/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const HoursPage = lazy(() => import('../pages/analytics/HoursPage').then((m) => ({ default: m.HoursPage })));
const AvailabilityPage = lazy(() => import('../pages/analytics/AvailabilityPage').then((m) => ({ default: m.AvailabilityPage })));
const ComparePage = lazy(() => import('../pages/analytics/ComparePage').then((m) => ({ default: m.ComparePage })));
const LegsPage = lazy(() => import('../pages/analytics/LegsPage').then((m) => ({ default: m.LegsPage })));
const GoalsPage = lazy(() => import('../pages/analytics/GoalsPage').then((m) => ({ default: m.GoalsPage })));
const FinancePage = lazy(() => import('../pages/analytics/FinancePage').then((m) => ({ default: m.FinancePage })));
const TripsPage = lazy(() => import('../pages/analytics/TripsPage').then((m) => ({ default: m.TripsPage })));
const DirectoryPage = lazy(() => import('../pages/directory/DirectoryPage').then((m) => ({ default: m.DirectoryPage })));
const CrewSwapsPage = lazy(() => import('../pages/crewswap/CrewSwapsPage').then((m) => ({ default: m.CrewSwapsPage })));
const OnboardingPage = lazy(() => import('../pages/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const PeoplePage = lazy(() => import('../pages/portal/PeoplePage').then((m) => ({ default: m.PeoplePage })));
const SchedulePage = lazy(() => import('../pages/schedule/SchedulePage').then((m) => ({ default: m.SchedulePage })));
const PilotsPage = lazy(() => import('../pages/pilots/PilotsPage').then((m) => ({ default: m.PilotsPage })));
const PilotPage = lazy(() => import('../pages/pilots/PilotPage').then((m) => ({ default: m.PilotPage })));
const CustomersPage = lazy(() => import('../pages/customers/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const FleetExpansionPage = lazy(() => import('../pages/expansion/FleetExpansionPage').then((m) => ({ default: m.FleetExpansionPage })));
const UnavailabilityPage = lazy(() => import('../pages/unavailability/UnavailabilityPage').then((m) => ({ default: m.UnavailabilityPage })));
const FleetPage = lazy(() => import('../pages/fleet/FleetPage').then((m) => ({ default: m.FleetPage })));
const HistoryPage = lazy(() => import('../pages/history/HistoryPage').then((m) => ({ default: m.HistoryPage })));
const UtilizationPage = lazy(() => import('../pages/utilization/UtilizationPage').then((m) => ({ default: m.UtilizationPage })));
const SettingsPage = lazy(() => import('../pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const MySchedulePage = lazy(() => import('../pages/my-schedule/MySchedulePage').then((m) => ({ default: m.MySchedulePage })));

/**
 * Inside the scheduler module: the page's minimum role against the person's
 * scheduler role. On a phone the module is My schedule only, whatever the
 * role (D85).
 */
function Gate({ page, children }: { page: PageId; children: ReactNode }) {
  const { role } = useAuth();
  const phone = useIsPhone();
  const def = PAGES.find((p) => p.id === page);
  if (!role || !def) return null;
  if (phone && page !== 'my-schedule') return <Navigate to={homePathFor(role, true)} replace />;
  if (!canSee(role, def)) return <Navigate to={homePathFor(role, phone)} replace />;
  return <>{children}</>;
}

/** At a module's door (D83): no role in it, or not built yet, means back to the portal home. */
function ModuleGate({ module, children }: { module: PortalModule; children: ReactNode }) {
  const { access, portalAdmin } = useAuth();
  const def = moduleDef(module);
  // A role in a module embedded here (Financials inside Analytics) opens the door too (D104).
  if (!def.enabled || !visibleModules(access, portalAdmin).some((m) => m.key === module)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PortalAdminGate({ children }: { children: ReactNode }) {
  const { portalAdmin } = useAuth();
  if (!portalAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Old scheduler addresses (pre-D83) keep working, sub-path and query string included. */
function LegacyRedirect({ to }: { to: string }) {
  const loc = useLocation();
  const rest = useParams()['*'] ?? '';
  return <Navigate to={`${to}${rest ? '/' + rest : ''}${loc.search}`} replace />;
}

/**
 * Signing in always lands on the portal home (D85). While nobody is signed
 * in, the address is reset to /, so whichever page someone signed out from,
 * the next sign-in starts at the tiles. Only the path is reset: a sign-in
 * code in the query string is left for the auth client. A refresh with a
 * live session is not a sign-in and keeps its place.
 */
function AtRoot({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  if (pathname !== '/') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const auth = useAuth();
  const phone = useIsPhone();

  if (auth.status === 'unconfigured') return <SetupPage />;
  if (auth.status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Signing you in" />
      </div>
    );
  }
  if (auth.status === 'signed-out') return <AtRoot><SignInPage /></AtRoot>;
  if (auth.status === 'recovery') return <AtRoot><ResetPasswordPage /></AtRoot>;
  if (auth.status === 'no-membership') return <AtRoot><NoAccessPage /></AtRoot>;

  const schedulerHome = auth.role ? homePathFor(auth.role, phone) : '/';
  return (
    <DrawerProvider>
      <Routes>
        <Route element={<PortalShell />}>
          <Route index element={<PortalHome />} />
          <Route path="people" element={<PortalAdminGate><PeoplePage /></PortalAdminGate>} />
          <Route path="documents" element={<ModuleGate module="documents"><DocumentsPage /></ModuleGate>} />
          <Route path="documents/:id" element={<ModuleGate module="documents"><DocumentPage /></ModuleGate>} />
          <Route path="directory" element={<ModuleGate module="directory"><DirectoryPage /></ModuleGate>} />
          <Route path="crewswap" element={<ModuleGate module="crewswap"><CrewSwapsPage /></ModuleGate>} />
          <Route path="onboarding/*" element={<ModuleGate module="onboarding"><OnboardingPage /></ModuleGate>} />
          <Route path="customers/*" element={<ModuleGate module="customers"><CustomersPage /></ModuleGate>} />
          <Route path="expansion/*" element={<ModuleGate module="expansion"><FleetExpansionPage /></ModuleGate>} />
          <Route path="analytics" element={<ModuleGate module="analytics"><AnalyticsLayout /></ModuleGate>}>
            <Route index element={<Navigate to="/analytics/hours" replace />} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path="trips" element={<TripsPage />} />
            <Route path="hours" element={<HoursPage />} />
            <Route path="availability" element={<AvailabilityPage />} />
            <Route path="compare" element={<ComparePage />} />
            <Route path="legs" element={<LegsPage />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="finance" element={<FinancePage />} />
          </Route>
        </Route>

        <Route path="scheduler" element={<ModuleGate module="scheduler"><Layout /></ModuleGate>}>
          <Route index element={<Navigate to={schedulerHome} replace />} />
          <Route path="schedule" element={<Gate page="schedule"><SchedulePage /></Gate>} />
          <Route path="pilots" element={<Gate page="pilots"><PilotsPage /></Gate>} />
          <Route path="pilots/:code" element={<Gate page="pilots"><PilotPage /></Gate>} />
          <Route path="unavailability" element={<Gate page="unavailability"><UnavailabilityPage /></Gate>} />
          <Route path="fleet" element={<Gate page="fleet"><FleetPage /></Gate>} />
          <Route path="published" element={<Gate page="history"><HistoryPage /></Gate>} />
          <Route path="utilization" element={<Gate page="utilization"><UtilizationPage /></Gate>} />
          <Route path="settings" element={<Gate page="settings"><SettingsPage /></Gate>} />
          <Route path="me" element={<MySchedulePage />} />
          <Route path="*" element={<Navigate to={schedulerHome} replace />} />
        </Route>

        {/* Old addresses, kept so bookmarks and the pilots' calendar links survive. */}
        <Route path="schedule/*" element={<LegacyRedirect to={`${SCHEDULER_BASE}/schedule`} />} />
        <Route path="published/*" element={<LegacyRedirect to={`${SCHEDULER_BASE}/published`} />} />
        <Route path="history/*" element={<LegacyRedirect to={`${SCHEDULER_BASE}/published`} />} />
        <Route path="pilots/*" element={<LegacyRedirect to={`${SCHEDULER_BASE}/pilots`} />} />
        <Route path="fleet/*" element={<LegacyRedirect to={`${SCHEDULER_BASE}/fleet`} />} />
        <Route path="utilization/*" element={<LegacyRedirect to={`${SCHEDULER_BASE}/utilization`} />} />
        <Route path="settings/*" element={<LegacyRedirect to={`${SCHEDULER_BASE}/settings`} />} />
        <Route path="me" element={<LegacyRedirect to={`${SCHEDULER_BASE}/me`} />} />
        <Route path="rules" element={<Navigate to={`${SCHEDULER_BASE}/pilots?tab=rules`} replace />} />
        <Route path="starting-point" element={<Navigate to={`${SCHEDULER_BASE}/fleet`} replace />} />
        <Route path="violations" element={<Navigate to={`${SCHEDULER_BASE}/schedule`} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </DrawerProvider>
  );
}
